import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import {
  BEZPIECZNIK_MS,
  COUNTUP_MS,
  kwantyzuj,
  rozbierzLiczbe,
  ruchDozwolony,
  wyhamowanie,
  wypiszKlatke,
  zapiszKlatke,
  type Klatka,
} from '@/lib/liczbaAnimowana';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * COUNT-UP LICZB — AnimatedNumber (R5-15, 2026-10-04)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Liczba wjeżdża od 0 do wartości ze źródła w ~800 ms z wyhamowaniem, jak w aplikacji 4.0.
 *
 * KONTRAKT
 *  • Formater działa w KAŻDEJ klatce („0,00 zł → 84 360,00 zł”, „0% → 19%”, „0 → 1284”). Klatki
 *    są skwantowane do kroku wartości — bez artefaktów precyzji.
 *  • Na końcu stoi DOKŁADNIE to, co formater zwraca dla wartości źródłowej, a nie wynik ostatniej
 *    klatki. Po animacji DOM jest taki sam jak bez niej: sam tekst, bez nakładek.
 *  • Brak wartości (ładowanie, brak danych) NIE jest zerem: zastępnik, nigdy „0”.
 *  • Animacja startuje przy WEJŚCIU wartości na ekran. Ponowny render i refetch z tą samą liczbą
 *    jej nie restartują (klucz = wartość). Wyjście z widoku i powrót = nowe zamontowanie = nowa
 *    animacja. Nowa wartość na tym samym ekranie (np. inny tydzień) liczy się od 0.
 *  • `prefers-reduced-motion: reduce` albo karta w tle → od razu wartość końcowa. Gdy zegar klatek
 *    stoi (okno nierysowane), bezpiecznik pokazuje wartość końcową po czasie animacji + 1 s —
 *    warstwa klatek nigdy nie zostaje na „0”.
 *  • Czytnik ekranu dostaje wyłącznie wartość końcową; klatki i miarka są `aria-hidden`.
 *  • Wydajność: jeden wspólny `requestAnimationFrame` na cały ekran (`lib/liczbaAnimowana.ts`).
 *    Klatki piszą tekst wprost do DOM, bez stanu React — strona nie renderuje się co klatkę.
 *    Komponent renderuje się dwa razy: przy wejściu i po ostatniej klatce.
 *  • Szerokość jest zarezerwowana od pierwszej klatki przez niewidoczną miarkę z wartością
 *    końcową, więc kolumny tabel i sąsiednie elementy nie „pływają” w trakcie liczenia.
 *
 * NIE animujemy wierszy dużych tabel — tylko karty KPI, podsumowania, sumy i liczniki.
 */

export type AnimatedNumberProps = {
  /** Wartość ze źródła. `null`/`undefined` = nie znamy (ładowanie, brak danych) — nigdy 0. */
  value: number | null | undefined;
  /** Formater używany w każdej klatce i dla wartości końcowej. */
  format: (n: number) => string;
  /**
   * Krok klatek pośrednich: 1 = liczby całkowite (domyślnie), 0.01 = setne części,
   * 100 = pełne złote przy kwocie w groszach (np. `formatZlote`).
   */
  step?: number;
  durationMs?: number;
  /** Co pokazać, gdy wartości nie znamy. Domyślnie „—”. */
  placeholder?: ReactNode;
  /**
   * 'svg' — wewnątrz `<text>` wykresu: klatki w `<tspan>`, bez miarki i warstwy dla czytnika,
   * bo rysunek SVG ma własną etykietę dostępności z wartością końcową.
   */
  wariant?: 'html' | 'svg';
};

export function AnimatedNumber({ value, placeholder = '—', ...reszta }: AnimatedNumberProps) {
  if (value === null || value === undefined || !Number.isFinite(value)) return <>{placeholder}</>;
  // KLUCZ = WARTOŚĆ. Ta sama liczba przy ponownym renderze albo refetchu nie zmienia klucza, więc
  // niczego nie restartuje. Nowa liczba to nowe wejście na ekran, więc liczy się od 0.
  return <Licznik key={value} value={value} {...reszta} />;
}

/**
 * Wariant dla wartości JUŻ SFORMATOWANEJ (karty KPI dostają tekst, nie liczbę). Tekst, który nie
 * jest jedną liczbą („—”, „brak danych”, data, „3 z 5”), zostaje wyświetlony bez zmian.
 */
export function AnimatedNumberText({
  text,
  wariant,
  durationMs,
}: {
  text: string;
  wariant?: 'html' | 'svg';
  durationMs?: number;
}) {
  const rozbior = useMemo(() => rozbierzLiczbe(text), [text]);
  if (!rozbior) return <>{text}</>;
  return (
    <AnimatedNumber
      value={rozbior.wartosc}
      format={rozbior.format}
      step={rozbior.krok}
      wariant={wariant}
      durationMs={durationMs}
    />
  );
}

/* Kontener zajmuje miejsce wartości KOŃCOWEJ (miarka), a klatki leżą na nim warstwą absolutną —
   zmiana tekstu klatki nie przelicza układu wokół. Wyrównanie tekstu klatki dziedziczy się
   z komórki (np. do prawej w tabeli). */
const KONTENER: CSSProperties = { position: 'relative', display: 'inline-block' };
const MIARKA: CSSProperties = { visibility: 'hidden' };
const KLATKA: CSSProperties = { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0 };
const DLA_CZYTNIKA: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
  border: 0,
};

function Licznik({
  value,
  format,
  step = 1,
  durationMs = COUNTUP_MS,
  wariant = 'html',
}: Omit<AnimatedNumberProps, 'value' | 'placeholder'> & { value: number }) {
  // Decyzja zapada RAZ, przy wejściu wartości na ekran: przy zerze nie ma czego liczyć,
  // a przy ograniczonym ruchu (albo bez zegara klatek) pokazujemy od razu wynik.
  const [skonczona, setSkonczona] = useState(() => value === 0 || !ruchDozwolony());
  const klatkaHtml = useRef<HTMLSpanElement>(null);
  const klatkaSvg = useRef<SVGTSpanElement>(null);

  // Najnowszy formater bez restartu animacji: rodzic może podać nową funkcję przy każdym renderze.
  const formatRef = useRef(format);
  useEffect(() => {
    formatRef.current = format;
  });

  useEffect(() => {
    if (skonczona) return undefined;
    const el = wariant === 'svg' ? klatkaSvg.current : klatkaHtml.current;
    if (!el) return undefined;
    let start: number | null = null;
    const klatka: Klatka = (teraz) => {
      if (start === null) start = teraz;
      const postep = Math.min(1, (teraz - start) / durationMs);
      if (postep >= 1) {
        setSkonczona(true);
        return true;
      }
      el.textContent = formatRef.current(kwantyzuj(value * wyhamowanie(postep), step, value));
      return false;
    };
    zapiszKlatke(klatka);
    // Zegar klatek może stać (okno nierysowane) — wtedy wynik końcowy i tak musi się pojawić.
    const bezpiecznik = window.setTimeout(() => {
      wypiszKlatke(klatka);
      setSkonczona(true);
    }, durationMs + BEZPIECZNIK_MS);
    return () => {
      window.clearTimeout(bezpiecznik);
      wypiszKlatke(klatka);
    };
  }, [skonczona, value, step, durationMs, wariant]);

  const koncowy = format(value);
  if (skonczona) return <>{koncowy}</>;

  const poczatkowy = format(kwantyzuj(0, step, value));
  if (wariant === 'svg') {
    return (
      <tspan ref={klatkaSvg} aria-hidden="true" data-licznik="klatka">
        {poczatkowy}
      </tspan>
    );
  }
  return (
    <span style={KONTENER} data-licznik="kontener">
      <span aria-hidden="true" style={MIARKA} data-licznik="miarka">
        {koncowy}
      </span>
      <span aria-hidden="true" style={KLATKA} ref={klatkaHtml} data-licznik="klatka">
        {poczatkowy}
      </span>
      <span style={DLA_CZYTNIKA} data-licznik="czytnik">
        {koncowy}
      </span>
    </span>
  );
}
